import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { getProject, createOverview, updateOverview } from "../../api/overview";
import TmapGeocoder from "../../components/viewer/tmap/TmapGeocoder";
import ImageModal from "../../components/common/ui/ImageModal";
import { Carousel } from "../../components/common/viewer/Carousel";
import TableCell from "../../components/common/ui/TableCell";
import PageHeader from "../../components/common/ui/PageHeader";
import { formatDate } from "../../utils/date";

/* =========================
 * Constants & Utilities
 * ========================= */
const EMPTY_OVERVIEW = {
  project_name: "",
  client: "",
  CM_supervision: "",
  CM_supervision_role: "CM/감리단",
  designer: "",
  constructor: "",
  types: "architecture",
  total_cost: "",
  contract_date: "",
  construction_date: "",
  estimated_completion_date: "",
  completion_date: "",
  site_location: "",
  business_scale: "",
};

const REQUIRED_FIELDS = [
  "project_name",
  "client",
  "CM_supervision",
  "constructor",
  "site_location",
];

const NUMBER_FIELDS = ["total_cost"];
const DECIMAL_PLACES = { total_cost: 1 };

// 표시용: 정수면 소수부 숨김, 소수면 말미 0 제거 + 천단위 콤마
const formatForDisplay = (raw) => {
  const s = String(raw ?? "")
    .replace(/,/g, "")
    .trim();
  if (!s) return "";
  if (!/^\d+(\.\d+)?$/.test(s)) return String(raw);

  const [intPart, decPart = ""] = s.split(".");
  const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
  const trimmedDec = decPart.replace(/0+$/, "");
  return trimmedDec ? `${formattedInt}.${trimmedDec}` : formattedInt;
};

const toStr = (v) => (v === null || v === undefined ? "" : String(v));
const safeValue = (v) => {
  if (v === null || v === undefined) return "";
  if (typeof v === "number" && Number.isNaN(v)) return "";
  if (typeof v !== "string" && typeof v !== "number") return "";
  return String(v);
};

// 다운로드 파일명 생성
const getDownloadName = (item, idx) => {
  const rawNameFromItem = item.original_name || item.filename || "";
  let raw = "";
  try {
    const u = new URL(item.url, window.location.origin);
    raw = decodeURIComponent(u.pathname.split("/").pop() || "");
  } catch {}
  const candidate = rawNameFromItem || raw || `birdseye-${idx + 1}.jpg`;
  const dot = candidate.lastIndexOf(".");
  const base = dot > -1 ? candidate.slice(0, dot) : candidate;
  const ext = dot > -1 ? candidate.slice(dot) : "";
  const cleanedBase = base
    .replace(/([_\-\.])(?:[0-9a-f]{8,36})$/i, "")
    .replace(/([_\-\.])[A-Za-z0-9]{6,}$/i, "");
  const safe = (cleanedBase || base).replace(/[\/\\:*?"<>|]+/g, "_").trim();
  return (safe || `birdseye-${idx + 1}`) + (ext || ".jpg");
};

const forceDownload = async (url, filename) => {
  try {
    const res = await fetch(url, { credentials: "omit" });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const blobUrl = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = blobUrl;
    a.download = filename || "download";
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(blobUrl);
  } catch {
    window.open(url, "_blank", "noopener");
  }
};

/* =========================
 * Reusable Inputs
 * ========================= */

// 공통 숫자 입력 (콤마, 소수 1자리 제한, onBlur에 display format)
const NumberInput = ({
  value,
  onChange,
  onBlur,
  readonly,
  className,
  style,
}) => (
  <input
    type="text"
    value={safeValue(value)}
    onChange={onChange}
    onBlur={onBlur}
    readOnly={readonly}
    inputMode="decimal"
    className={className}
    style={style}
  />
);

// 숫자 입력(단위 포함, 가로 폭 자동)
const NumberInputWithUnit = ({
  value,
  onChange,
  onBlur,
  readonly,
  numberAutoCls,
  getAutoSize,
  unit = "㎡",
  inputAlign = "left", // "left" | "center" | "right"
}) => {
  // 조회/수정 모두 값 길이에 맞춰 폭 자동 조절
  // - 수정 모드는 border/padding 때문에 조금 더 여유 폭을 준다
  const widthCh = getAutoSize
    ? getAutoSize(
        value,
        readonly ? 7 : 8, // 최소 폭: 조회 7ch, 수정 8ch
        readonly ? 2 : 4 // 여유 폭: 조회 2ch, 수정 4ch
      )
    : undefined;
  const style = widthCh ? { width: `${widthCh}ch` } : undefined;
  const alignCls =
    inputAlign === "center"
      ? "text-center"
      : inputAlign === "right"
      ? "text-right"
      : "";

  return (
    <>
      <NumberInput
        value={value}
        onChange={onChange}
        onBlur={onBlur}
        readonly={readonly}
        className={`${numberAutoCls} ${alignCls}`}
        style={style}
      />
      <span className="shrink-0 pl-1">{unit}</span>
    </>
  );
};

/* =========================
 * Main Component
 * ========================= */
const OverviewRegister = ({
  is_superuser,
  canManageOverview: canManageOverviewProp,
  initialForm,
  readonly = false,
  viewActions,
}) => {
  const [canManageOverview, setCanManageOverview] = useState(
    Boolean(canManageOverviewProp)
  );

  // prop로 넘어오면 우선 적용
  useEffect(() => {
    if (canManageOverviewProp !== undefined) {
      setCanManageOverview(Boolean(canManageOverviewProp));
    }
  }, [canManageOverviewProp]);

  const navigate = useNavigate();
  const { id: projectId } = useParams();
  const location = useLocation();

  // Layout classes
  const viewInputCls =
    "w-full bg-transparent border-0 px-2 py-0 focus:outline-none focus:ring-0 text-gray-900 text-left";
  const editInputCls = "input-base";

  const viewNumberAutoCls =
    "bg-transparent border-0 px-1 py-0 focus:outline-none focus:ring-0 text-gray-900 w-auto min-w-[4ch]";
  const editNumberAutoCls = "input-base w-auto min-w-[4ch]";
  const numberAutoCls = readonly ? viewNumberAutoCls : editNumberAutoCls;

  const getAutoSize = (v, min = 1, pad = 0) => {
    // 표시 문자열 그대로의 길이(콤마 포함)를 기준으로 폭 계산
    const s = String(v ?? "");
    return Math.max(min, s.length + pad);
  };

  const formFromState = location.state?.initialForm;
  const [form, setForm] = useState(
    formFromState ?? initialForm ?? { ...EMPTY_OVERVIEW }
  );
  const [touched, setTouched] = useState({});

  const [imgOpen, setImgOpen] = useState(false);
  const [imgSrc, setImgSrc] = useState("");
  const [gallery, setGallery] = useState([]); // [{id?, url, file?}]
  const [deletedIds, setDeletedIds] = useState([]);
  const fileRef = useRef(null);

  // ===== Height sync (조감도 ↔ 기본정보, 현장지도 ↔ 사업규모) =====
  const birdseyeBoxRef = useRef(null);
  const mapBoxRef = useRef(null);
  const [basicInfoHeight, setBasicInfoHeight] = useState(null);
  const [businessScaleHeight, setBusinessScaleHeight] = useState(null);

  useLayoutEffect(() => {
    const sync = () => {
      if (birdseyeBoxRef.current) {
        setBasicInfoHeight(birdseyeBoxRef.current.offsetHeight);
      }
      if (mapBoxRef.current) {
        setBusinessScaleHeight(mapBoxRef.current.offsetHeight);
      }
    };
    sync();

    let ro;
    if (typeof window !== "undefined" && "ResizeObserver" in window) {
      ro = new ResizeObserver(sync);
      if (birdseyeBoxRef.current) ro.observe(birdseyeBoxRef.current);
      if (mapBoxRef.current) ro.observe(mapBoxRef.current);
    }
    window.addEventListener("resize", sync);
    return () => {
      ro?.disconnect();
      window.removeEventListener("resize", sync);
    };
  }, [gallery.length, readonly, form.site_location]);

  // gallery 초기화
  useEffect(() => {
    const src = formFromState ?? initialForm ?? form ?? null;
    if (!src) return;
    const arr = Array.isArray(src.birdseye_images) ? src.birdseye_images : [];
    const mapped = arr.map((it) =>
      typeof it === "string" ? { url: it } : { id: it.id, url: it.url }
    );
    const single = src.birdseye_image ? [{ url: src.birdseye_image }] : [];
    setGallery(mapped.length ? mapped : single);
  }, [formFromState, initialForm]);

  const onPickFiles = (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    const news = files.map((f) => ({ file: f, url: URL.createObjectURL(f) }));
    setGallery((prev) => [...prev, ...news]);
    setForm((prev) => ({
      ...prev,
      birdseye_images: [
        ...(Array.isArray(prev.birdseye_images) ? prev.birdseye_images : []),
        ...files,
      ],
      birdseye_image: prev.birdseye_image || files[0] || "",
    }));
    e.target.value = "";
  };

  const removeAt = (i) => {
    if (!window.confirm("사진을 삭제하시겠습니까?")) return;
    setGallery((prev) => {
      const item = prev[i];
      if (item?.id) setDeletedIds((xs) => [...xs, item.id]);
      if (item?.file) {
        setForm((f) => ({
          ...f,
          birdseye_images: (Array.isArray(f.birdseye_images)
            ? f.birdseye_images
            : []
          ).filter((ff) => ff !== item.file),
        }));
      }
      return prev.filter((_, idx) => idx !== i);
    });
  };

  // textarea autoresize
  const bsRef = useRef(null);
  const autoResize = (el) => {
    if (!el) return;
    // 내용이 없을 때는 기본 CSS 높이(h-9)를 그대로 사용
    if (!el.value) {
      el.style.height = "";
      return;
    }
    // 내용이 있을 때만 내용 길이에 맞춰 세로 길이 자동 조절
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  };
  useEffect(() => {
    autoResize(bsRef.current);
  }, [form.business_scale, readonly]);

  // initialForm 적용 + 숫자/날짜 표시 포맷
  useEffect(() => {
    const source = initialForm ?? formFromState;
    if (!source) return;
    setForm((prev) => ({
      ...prev,
      ...Object.fromEntries(
        Object.entries(source).map(([k, v]) => {
          // 숫자 필드(총 사업비 등)는 조회 화면과 동일한 포맷(3자리 콤마)으로 맞춰서 세팅
          if (NUMBER_FIELDS.includes(k)) {
            return [k, formatForDisplay(v)];
          }

          // 날짜 필드는 문자열로만 정리
          if (
            [
              "contract_date",
              "construction_date",
              "estimated_completion_date",
              "completion_date",
            ].includes(k)
          ) {
            return [k, toStr(v)];
          }

          // 나머지는 그냥 문자열 변환
          return [k, toStr(v)];
        })
      ),
    }));
  }, [initialForm, formFromState]);

  // 프로젝트 기본정보 로드 (등록 모드)
  useEffect(() => {
    if (formFromState || initialForm || !projectId) return;
    getProject(projectId)
      .then((data) => {
        // ✅ 백엔드가 내려주는 staff 여부
        if (data?.can_manage_overview !== undefined) {
          setCanManageOverview(Boolean(data.can_manage_overview));
        }
        const participants = data?.participants || {};
        const requiredRoles = ["발주처", "CM/감리단", "시공사"];
        const missingRoles = requiredRoles.filter(
          (role) => !participants[role]
        );
        // if (missingRoles.length > 0) {
        //   alert(
        //     `아래 참여주체가 등록되어 있지 않습니다:\n${missingRoles.join(
        //       ", "
        //     )}\n\n설정을 눌러 '조직 관리 - 회사 등록' 페이지에서\n프로젝트 참여 매핑에서 역할을 매칭한 후,\n\n사업개요를 등록해주세요.`
        //   );
        //   navigate(`/project/${projectId}/system/user/pcp`);
        //   return;
        // }

        // 설계사가 매핑되어 있지 않은 경우, 안내만 띄우고 페이지는 그대로 진입
        if (!participants["설계사"]) {
          alert(
            "설계사가 없습니다.\n\n추가하고 싶다면 프로젝트 참여주체(역할·매핑) 관리 페이지에서\n역할을 추가한 뒤 회사를 등록해주세요."
          );
        }

        setForm((prev) => ({
          ...prev,
          project_name: toStr(data?.name),
          site_location: toStr(data?.address),
          client: toStr(participants["발주처"]),
          CM_supervision: toStr(
            participants["CM/감리단"] ?? participants["PM단"]
          ),
          CM_supervision_role: participants["CM/감리단"]
            ? "CM/감리단"
            : participants["PM단"]
            ? "PM단"
            : "CM/감리단",
          designer: participants["설계사"] ? toStr(participants["설계사"]) : "",
          constructor: toStr(participants["시공사"]),
        }));
      })
      .catch((err) => {
        console.error("프로젝트 불러오기 실패:", err);
        alert("프로젝트 정보를 불러오는 데 실패했습니다.");
      });
  }, [formFromState, initialForm, projectId, navigate]);

  const onChange = (key) => (e) => {
    if (readonly) return;
    const v = e.target.value;
    setForm((prev) => ({ ...prev, [key]: safeValue(v) }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  // 숫자 입력 (공통)
  const onNumberChange = (key) => (e) => {
    if (readonly) return;
    let value = e.target.value.replace(/,/g, "");
    if (!/^(\d+)?(\.\d*)?$/.test(value) && value !== "") {
      alert("숫자를 입력해주세요.");
      return;
    }
    if (value.includes(".")) {
      const decPart = value.split(".")[1];
      // 소수부는 1자리까지만 허용
      if (decPart && decPart.length > 1) {
        alert("소수 1째자리까지만 입력할 수 있습니다.");
        return;
      }
    }
    if (value) {
      const [intPart, decPart] = value.split(".");
      const formattedInt = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");
      value =
        decPart !== undefined ? `${formattedInt}.${decPart}` : formattedInt;
    }
    setForm((prev) => ({ ...prev, [key]: toStr(value) }));
    setTouched((prev) => ({ ...prev, [key]: true }));
  };

  // 포커스 아웃 시 표시 포맷(정수면 소수부 숨김)
  const onNumberBlur = (key) => (e) => {
    if (readonly) return;
    setForm((prev) => ({ ...prev, [key]: formatForDisplay(e.target.value) }));
  };

  const isRequiredInvalid = (key) =>
    REQUIRED_FIELDS.includes(key) && touched[key] && !form[key];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (readonly) return;

    const invalid = REQUIRED_FIELDS.filter((key) => !form[key]);
    if (invalid.length > 0) {
      setTouched((prev) => {
        const updated = { ...prev };
        invalid.forEach((key) => (updated[key] = true));
        return updated;
      });
      alert("필수 항목을 채워주세요. (해당 없는 항목은 '-' 입력 가능)");
      return;
    }

    try {
      const submitForm = { ...form, project: projectId };

      if (!submitForm.types) {
        submitForm.types = "architecture";
      }

      if (Array.isArray(form.birdseye_images) && form.birdseye_images.length) {
        submitForm.birdseye_images = form.birdseye_images;
        submitForm.birdseye_image =
          form.birdseye_images[0] ?? form.birdseye_image;
      }
      if (deletedIds.length) {
        submitForm.delete_birdseye_image_ids = deletedIds;
      }

      NUMBER_FIELDS.forEach((key) => {
        let val = safeValue(submitForm[key]).replace(/,/g, "");
        if (val !== "") {
          const places = DECIMAL_PLACES[key] ?? 0;
          submitForm[key] =
            places === 0
              ? Number.parseInt(val, 10)
              : Number(val).toFixed(places);
        }
      });

      [
        "contract_date",
        "construction_date",
        "estimated_completion_date",
        "completion_date",
      ].forEach((k) => {
        submitForm[k] = submitForm[k] ? String(submitForm[k]) : null;
      });

      if (form.id) {
        await updateOverview(projectId, form.id, submitForm);
        alert("사업개요가 수정되었습니다.");
      } else {
        await createOverview(projectId, submitForm);
        alert("사업개요가 저장되었습니다.");
      }
      navigate(`/project/${projectId}/overview/view`);
    } catch (err) {
      console.error("API 오류:", err?.response?.data ?? err);
      alert("저장 중 오류가 발생했습니다.");
    }
  };

  const handleCancel = () => {
    if (
      window.confirm("변경사항이 저장되지 않습니다. 조회 페이지로 이동할까요?")
    ) {
      setForm(formFromState ?? initialForm ?? { ...EMPTY_OVERVIEW });
      setTouched({});
      navigate(`/project/${projectId}/overview/view`, { replace: true });
    }
  };

  /* =========================
   * Render
   * ========================= */
  return (
    <div className="p-6">
      {/* 페이지 제목 */}
      <PageHeader>사업개요 {readonly ? "조회" : "등록/수정"}</PageHeader>

      {/* ===== 전체 좌/우 Grid ===== */}
      <div>
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* ========== 왼쪽 패널 (조감도 + 현장위치) ========== */}
          <div className="lg:col-span-1">
            {/* 조감도 */}
            <div>
              <div className="section-header">조감도</div>
              <div className="section-body">
                <div ref={birdseyeBoxRef} className="h-64">
                  {readonly ? (
                    gallery.length ? (
                      <Carousel
                        items={gallery}
                        heightClass="h-full"
                        ariaLabel="조감도"
                        showArrows
                        renderItem={(item, i) => (
                          <div className="relative h-full w-full">
                            <button
                              type="button"
                              onClick={() => {
                                setImgSrc(item.url);
                                setImgOpen(true);
                              }}
                              onPointerDown={(e) => e.stopPropagation()}
                              data-carousel-interactive
                              className="h-full w-full"
                            >
                              <img
                                src={item.url}
                                alt=""
                                className="h-full w-full object-contain select-none"
                                draggable={false}
                              />
                            </button>

                            <button
                              type="button"
                              onClick={() =>
                                forceDownload(
                                  item.url,
                                  getDownloadName(item, i)
                                )
                              }
                              onPointerDown={(e) => e.stopPropagation()}
                              data-carousel-interactive
                              className="absolute right-2 bottom-2 px-2 py-1 text-xs rounded-md border bg-white shadow"
                              title="이미지 다운로드"
                            >
                              다운로드
                            </button>
                          </div>
                        )}
                      />
                    ) : (
                      <div className="h-full grid place-items-center text-slate-500 text-center">
                        등록된 이미지가 없습니다.
                      </div>
                    )
                  ) : (
                    <>
                      <Carousel
                        items={[...gallery, { __add: true }]}
                        heightClass="h-full"
                        showArrows
                        renderItem={(item, i) =>
                          item.__add ? (
                            <button
                              type="button"
                              aria-label="이미지 추가"
                              title="이미지 추가"
                              onClick={() => fileRef.current?.click()}
                              onPointerDown={(e) => e.stopPropagation()}
                              data-carousel-interactive
                              className="relative h-full w-full bg-slate-800/60 text-white/85 select-none"
                            >
                              <span className="absolute inset-0 grid place-items-center pointer-events-none">
                                <span className="text-7xl leading-none translate-y-0/2">
                                  ＋
                                </span>
                              </span>
                              <span className="absolute bottom-8 left-1/2 -translate-x-1/2 text-sm opacity-90">
                                이미지 추가
                              </span>
                            </button>
                          ) : (
                            <div className="relative h-full w-full bg-black/5">
                              <button
                                type="button"
                                onClick={() => {
                                  setImgSrc(item.url);
                                  setImgOpen(true);
                                }}
                                onPointerDown={(e) => e.stopPropagation()}
                                data-carousel-interactive
                                className="h-full w-full"
                              >
                                <img
                                  src={item.url}
                                  alt=""
                                  className="h-full w-full object-contain select-none"
                                  draggable={false}
                                />
                              </button>

                              <button
                                type="button"
                                onPointerDown={(e) => e.stopPropagation()}
                                onClick={() => removeAt(i)}
                                className="absolute right-2 top-2 w-8 h-8 rounded-full bg-black/50 text-white"
                                aria-label="이미지 삭제"
                              >
                                ×
                              </button>
                            </div>
                          )
                        }
                      />

                      <input
                        ref={fileRef}
                        type="file"
                        accept="image/*"
                        multiple
                        className="hidden"
                        onChange={onPickFiles}
                      />
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* 현장 위치 */}
          </div>

          {/* ========== 오른쪽 패널 (기본정보 + 대지현황 + 사업규모) ========== */}
          <div className="lg:col-span-2">
            {/* 기본 정보 */}
            <div>
              <div className="section-header">기본 정보</div>
              <div
                className="section-body overflow-auto"
                style={
                  basicInfoHeight ? { height: basicInfoHeight } : undefined
                }
              >
                <table className="w-full h-full border border-slate-300 text-sm border-collapse">
                  <tbody>
                    <tr>
                      <TableCell
                        label="사업명"
                        editing={false}
                        value={<>&nbsp;&nbsp;{safeValue(form.project_name)}</>}
                        textCenter={true}
                      />
                      <TableCell
                        label="현장 주소"
                        editing={false}
                        value={<>&nbsp;&nbsp;{safeValue(form.site_location)}</>}
                        textCenter={true}
                      />
                    </tr>
                    <tr>
                      <TableCell
                        label="발주처"
                        editing={false}
                        value={<>&nbsp;&nbsp;{safeValue(form.client)}</>}
                        textCenter={true}
                      />

                      <TableCell
                        label={form.CM_supervision_role || "CM/감리단"}
                        editing={false}
                        value={
                          <>&nbsp;&nbsp;{safeValue(form.CM_supervision)}</>
                        }
                        textCenter={true}
                      />
                    </tr>

                    <tr>
                      <TableCell
                        label="설계사"
                        editing={false}
                        value={<>&nbsp;&nbsp;{safeValue(form.designer)}</>}
                        textCenter={true}
                      />

                      <TableCell
                        label="시공사"
                        editing={false}
                        value={<>&nbsp;&nbsp;{safeValue(form.constructor)}</>}
                        textCenter={true}
                      />
                    </tr>

                    <tr>
                      <TableCell
                        label="총 사업비"
                        editing={!readonly}
                        colSpan={3}
                        value={
                          readonly ? (
                            // 조회 모드: 다른 필드와 동일하게 순수 텍스트로 표시
                            <>
                              &nbsp;&nbsp;
                              {safeValue(form.total_cost)}
                              &nbsp;원
                            </>
                          ) : (
                            // (혹시라도 editing=false로 내려오는 경우 대비)
                            <>
                              &nbsp;&nbsp;
                              <NumberInputWithUnit
                                value={form.total_cost}
                                onChange={onNumberChange("total_cost")}
                                onBlur={onNumberBlur("total_cost")}
                                readonly={readonly}
                                numberAutoCls={numberAutoCls}
                                getAutoSize={getAutoSize}
                                unit="원"
                                inputAlign="center"
                              />
                            </>
                          )
                        }
                        input={
                          <NumberInputWithUnit
                            value={form.total_cost}
                            onChange={onNumberChange("total_cost")}
                            onBlur={onNumberBlur("total_cost")}
                            readonly={readonly}
                            numberAutoCls={numberAutoCls}
                            getAutoSize={getAutoSize}
                            unit="원"
                            inputAlign="center"
                          />
                        }
                        textCenter={true}
                      />
                    </tr>

                    <tr>
                      <TableCell
                        label="계약일"
                        editing={!readonly}
                        value={
                          <>&nbsp;&nbsp;{formatDate(form.contract_date)}</>
                        }
                        input={
                          <input
                            type="date"
                            name="contract_date"
                            value={form.contract_date || ""}
                            onChange={onChange("contract_date")}
                            className="input-base"
                          />
                        }
                        textCenter={true}
                      />

                      <TableCell
                        label="착공일"
                        editing={!readonly}
                        value={
                          <>&nbsp;&nbsp;{formatDate(form.construction_date)}</>
                        }
                        input={
                          <input
                            type="date"
                            name="construction_date"
                            value={form.construction_date || ""}
                            onChange={onChange("construction_date")}
                            className="input-base"
                          />
                        }
                        textCenter={true}
                      />
                    </tr>

                    <tr>
                      <TableCell
                        label="준공예정일"
                        editing={!readonly}
                        value={
                          <>
                            &nbsp;&nbsp;
                            {formatDate(form.estimated_completion_date)}
                          </>
                        }
                        input={
                          <input
                            type="date"
                            name="estimated_completion_date"
                            value={form.estimated_completion_date || ""}
                            onChange={onChange("estimated_completion_date")}
                            className="input-base"
                          />
                        }
                        textCenter={true}
                      />

                      <TableCell
                        label="준공일"
                        editing={!readonly}
                        value={
                          <>&nbsp;&nbsp;{formatDate(form.completion_date)}</>
                        }
                        input={
                          <input
                            type="date"
                            name="completion_date"
                            value={form.completion_date || ""}
                            onChange={onChange("completion_date")}
                            className="input-base"
                          />
                        }
                        textCenter={true}
                      />
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <div>
            <div className="section-header">현장 위치</div>
            <div className="section-body">
              <div
                ref={mapBoxRef}
                className="relative overflow-hidden isolate rounded-xl"
              >
                <TmapGeocoder
                  mapId="overview_site_map"
                  address={form.site_location || ""}
                />
              </div>
              <p className="text-xs text-slate-500 mt-4">
                현장 주소를 기준으로 위치가 표시됩니다.
              </p>

              {/* ✅ 조회/편집 모두 버튼을 "현장 위치" 아래 왼쪽 정렬 */}
              {canManageOverview && (readonly ? !!viewActions : true) && (
                <div className="mt-3 flex gap-2 justify-start">
                  {readonly ? (
                    viewActions
                  ) : (
                    <>
                      <button
                        type="submit"
                        className="btn-save"
                        onClick={handleSubmit}
                      >
                        저장
                      </button>
                      <button
                        type="button"
                        className="btn-cancel"
                        onClick={handleCancel}
                      >
                        취소
                      </button>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* 사업 규모 */}
          <div className="lg:col-span-2">
            <div className="section-header">사업 규모</div>
            <div
              className="section-body overflow-auto"
              style={
                businessScaleHeight
                  ? { height: businessScaleHeight }
                  : undefined
              }
            >
              <table className="w-full h-full border border-slate-300 text-sm border-collapse">
                <tbody>
                  <tr>
                    <TableCell
                      label="내용"
                      editing={!readonly}
                      value={
                        <textarea
                          ref={bsRef}
                          value={safeValue(form.business_scale)}
                          readOnly={readonly}
                          rows={1}
                          className={`${viewInputCls} w-full resize-none overflow-hidden leading-relaxed`}
                          style={{ height: "auto" }}
                        />
                      }
                      input={
                        <textarea
                          ref={bsRef}
                          value={safeValue(form.business_scale)}
                          onChange={onChange("business_scale")}
                          onInput={(e) => autoResize(e.target)}
                          rows={1}
                          className={`${editInputCls} w-full resize-none overflow-hidden leading-relaxed`}
                          placeholder="사업 규모를 입력하세요."
                          style={{ height: "auto" }}
                        />
                      }
                      textCenter={true}
                    />
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
        {/* 오른쪽 패널 끝 */}
      </div>
      {/* Grid 끝 */}

      {/* 이미지 모달 */}
      <ImageModal
        open={imgOpen}
        src={imgSrc}
        alt="조감도"
        onClose={() => setImgOpen(false)}
      />
    </div>
  );
};

export default OverviewRegister;
